/**
 * Booking context, pure month arithmetic for the calendar's month view.
 *
 * Companion to `week.ts`, and it borrows that file's one important trick: all
 * maths happens at **noon UTC**, so adding a day never lands on the wrong side
 * of a boundary whatever the host timezone is. Dates are `YYYY-MM-DD` calendar
 * strings, the same block-date grain the rest of Booking uses.
 *
 * "What is today in Beirut" stays the caller's impure edge
 * (`scheduling.wallClockAt(new Date()).date`), exactly as it is for weeks.
 */

import { addDays, weekStartFor } from "./week";

function atNoonUtc(date: string): Date {
  return new Date(`${date}T12:00:00Z`);
}

function toDateString(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** The first day of the month containing `date`. */
export function monthStartFor(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

/**
 * Add `n` months (may be negative), clamping the day to the target month's
 * length. Without the clamp, "a month after 31 January" is 3 March, which is
 * not what anyone pressing Next on a calendar means.
 */
export function addMonths(date: string, n: number): string {
  const d = atNoonUtc(date);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + n);
  const lastDay = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0, 12),
  ).getUTCDate();
  d.setUTCDate(Math.min(day, lastDay));
  return toDateString(d);
}

/** Do these two dates fall in the same calendar month? */
export function isSameMonth(a: string, b: string): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

/**
 * The dates of the month grid containing `date`: whole Monday-start weeks
 * covering the month, so the first and last rows spill into the neighbouring
 * months. Always a whole number of weeks, so the grid never has a ragged edge.
 */
export function monthGrid(date: string): string[] {
  const first = monthStartFor(date);
  const start = weekStartFor(first);
  const lastOfMonth = addDays(addMonths(first, 1), -1);
  const end = addDays(weekStartFor(lastOfMonth), 6);

  const cells: string[] = [];
  for (let cursor = start; cursor <= end; cursor = addDays(cursor, 1)) {
    cells.push(cursor);
  }
  return cells;
}

/** "August 2026", for the range label above the grid. */
export function monthLabel(date: string): string {
  return atNoonUtc(date).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
